import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, Stack, TextField,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import type { SubcontractProjectOption } from '../../hooks/useSubcontractProjects';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

interface ProjectChooseDialogProps {
  open: boolean;
  projects: SubcontractProjectOption[];
  loading?: boolean;
  selectedCode?: string;
  title?: string;
  onClose: () => void;
  onSelect: (project: SubcontractProjectOption) => void;
}

const ProjectChooseDialog: React.FC<ProjectChooseDialogProps> = ({
  open, projects, loading = false, selectedCode, title = 'Select Project', onClose, onSelect,
}) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SubcontractProjectOption | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(projects.find((project) => project.code === selectedCode) || null);
  }, [open, projects, selectedCode]);

  const rows = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return projects;
    return projects.filter((project) =>
      project.code.toLowerCase().includes(search) || project.name.toLowerCase().includes(search)
    );
  }, [projects, query]);

  const columns = useMemo<ColDef<SubcontractProjectOption>[]>(() => [
    { field: 'code', headerName: 'Project Code', minWidth: 170, flex: 0.7 },
    { field: 'name', headerName: 'Project Name', minWidth: 320, flex: 1.5 },
  ], []);

  const choose = (project: SubcontractProjectOption | null) => {
    if (!project) return;
    onSelect(project);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: { xs: '92vh', sm: 620 } } }}>
      <DialogTitle sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton size="small" onClick={onClose} aria-label="Close project selection"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search project code or name..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <Button size="small" onClick={() => setQuery('')} disabled={!query}>Clear</Button>
        </Stack>
        <Box className="ag-theme-alpine" sx={{ flex: 1, minHeight: 360, width: '100%', position: 'relative' }}>
          {loading && rows.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}><CircularProgress size={28} /></Stack>
          ) : (
            <AgGridReact<SubcontractProjectOption>
              rowData={rows}
              columnDefs={columns}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              getRowId={(params) => params.data.code}
              rowSelection={{ mode: 'singleRow', checkboxes: false, enableClickSelection: true }}
              pagination
              paginationPageSize={25}
              paginationPageSizeSelector={[25, 50, 100]}
              onSelectionChanged={(event) => setSelected(event.api.getSelectedRows()[0] || null)}
              onRowDoubleClicked={(event) => choose(event.data || null)}
              overlayNoRowsTemplate="No projects available for the selected company"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => choose(selected)} disabled={!selected}>Choose</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectChooseDialog;
